import {
  Entitlements,
  GoalOverviewRoadmapViewOptions,
  initialWeekStartsOn,
  InsightsTimeframes,
  insightsTimeframes,
  Interval,
  LimitKeys,
  SavedUserSubscription,
  SortingMode,
  TodayGroupBy,
  User,
  UserLimits,
} from '@griply/types';
import { getLimits, getPremiumLimits } from '@griply/utils';
import {
  addYears,
  differenceInDays,
  endOfDay,
  getYear,
  startOfDay,
} from 'date-fns';

import { DocumentSnapshot } from '../types/firestore';
import { coerceCustomTaskOrder } from './custom-task-order';
import {
  coerceFilterDocument,
  coerceGoalFilterDocument,
} from './filter-document';
import {
  firebaseObjectTimestampToDate,
  firebaseTimestampToDate,
} from './firebase-timestamp-to-date';
import { reconcileCustomTaskOrder } from './reconcile-custom-task-order';

const todayGroupByOptions = Object.values(TodayGroupBy);

type RawLimitEntry = {
  enforcedAt?: unknown;
  limit?: number;
};

const processLimits = (
  limits: Record<string, RawLimitEntry> | null | undefined,
  isPremium: boolean,
): UserLimits => {
  const limitBase = isPremium ? getPremiumLimits() : getLimits();
  return Object.keys(limitBase).reduce<UserLimits>((acc, limitKey) => {
    const key = limitKey as LimitKeys;
    const userLimit = limits?.[key];
    const limitBackup = limitBase[key];

    acc[key] = {
      limit: userLimit?.limit ?? limitBackup.limit,
      enforcedAt: userLimit?.enforcedAt
        ? firebaseTimestampToDate(userLimit.enforcedAt as Date) ?? new Date()
        : new Date(),
    };

    return acc;
  }, {} as UserLimits);
};

const coerceStringTimestamp = (
  value: string | Date | null | undefined,
): Date | null => {
  if (value === null || value === undefined) {
    return null;
  }
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const processSubscription = (subscription: SavedUserSubscription) => {
  const purchasedAt = coerceStringTimestamp(subscription.purchasedAt);
  const expiresAt = coerceStringTimestamp(subscription.expiresAt);
  const unsubscribedAt = coerceStringTimestamp(subscription.unsubscribedAt);
  const refundedAt = coerceStringTimestamp(subscription.refundedAt);

  const subscriptionDuration =
    expiresAt && purchasedAt
      ? differenceInDays(endOfDay(expiresAt), startOfDay(purchasedAt))
      : Infinity;

  const interval = !expiresAt
    ? Interval.Lifetime
    : subscriptionDuration <= 7
      ? Interval.Week
      : subscriptionDuration <= 14
        ? Interval.TwoWeeks
        : subscriptionDuration <= 31
          ? Interval.Month
          : subscriptionDuration > 3_650
            ? Interval.Lifetime
            : Interval.Year;

  return {
    type: subscription.type,
    store: subscription.store,
    stripeCustomerId: subscription.stripeCustomerId ?? undefined,
    managementUrl: subscription.managementUrl,
    productId: subscription.productId,
    purchasedAt: purchasedAt ?? new Date(0),
    expiresAt: interval === Interval.Lifetime ? null : expiresAt,
    unsubscribedAt: unsubscribedAt ?? undefined,
    refundedAt: refundedAt ?? undefined,
    interval: subscription.productId?.includes('custom')
      ? Interval.Lifetime
      : interval,
  };
};

export const userConverter = {
  fromFirestore: (snapshot: DocumentSnapshot): User => {
    const data = firebaseObjectTimestampToDate(snapshot.data()) as Record<
      string,
      unknown
    >;

    const settings = (data.settings ?? {}) as Record<string, unknown>;
    const rawFilters = settings.filters;
    const filtersIsObject =
      rawFilters !== null &&
      rawFilters !== undefined &&
      typeof rawFilters === 'object' &&
      !Array.isArray(rawFilters);
    const filtersRecord = filtersIsObject
      ? (rawFilters as Record<string, unknown>)
      : null;
    const coercedFilters =
      rawFilters === undefined
        ? undefined
        : filtersRecord === null
          ? null
          : {
              today: coerceFilterDocument(filtersRecord.today),
              allTasks: coerceFilterDocument(filtersRecord.allTasks),
              inbox: coerceFilterDocument(filtersRecord.inbox),
              goalsOverview: coerceGoalFilterDocument(
                filtersRecord.goalsOverview,
              ),
            };

    const coercedCustomTaskOrder = reconcileCustomTaskOrder(
      coerceCustomTaskOrder(settings.customTaskOrder),
      settings,
      new Date(),
    );

    const goalTabSorting = (settings.goalTabSorting ?? {}) as Record<
      string,
      { sorting?: unknown }
    >;
    const insightsSettings = (settings.insights ?? {}) as Record<
      string,
      unknown
    >;
    const insightsTimeframe = insightsSettings.timeframe as
      | InsightsTimeframes
      | null
      | undefined;
    const todayGroupByValue = settings.todayGroupBy as TodayGroupBy | undefined;

    const goalsOverviewSettings = (settings.goalsOverview ?? {}) as Record<
      string,
      unknown
    >;
    const goalOverviewViewOptions = Object.values(
      GoalOverviewRoadmapViewOptions,
    );
    const coerceGoalOverviewView = (value: unknown) =>
      goalOverviewViewOptions.includes(value as GoalOverviewRoadmapViewOptions)
        ? (value as GoalOverviewRoadmapViewOptions)
        : undefined;

    const entitlements = (data.entitlements ?? []) as Entitlements[];
    const isPremium = entitlements.includes(Entitlements.Premium);

    return {
      ...data,
      name: data.name ?? '',
      settings: {
        ...settings,
        startOfWeek: settings.startOfWeek ?? initialWeekStartsOn,
        goalTabSorting: {
          ...goalTabSorting,
          all: {
            sorting: settings.goalSorting,
            ...(goalTabSorting.all ?? {}),
          },
        },
        goalRoadmapSorting: settings.goalRoadmapSorting ?? {
          inProgress: goalTabSorting.active?.sorting ?? [],
          [getYear(new Date())]:
            goalTabSorting[getYear(new Date())]?.sorting ?? [],
          [getYear(addYears(new Date(), 1))]:
            goalTabSorting[getYear(new Date())]?.sorting ?? [],
        },
        todayGroupBy: todayGroupByOptions.includes(todayGroupByValue!)
          ? todayGroupByValue
          : TodayGroupBy.None,
        todoSortingMode: settings.todoSortingMode ?? SortingMode.DueDate,
        insights: {
          ...insightsSettings,
          timeframe: insightsTimeframe
            ? insightsTimeframes.includes(insightsTimeframe)
              ? insightsTimeframe
              : InsightsTimeframes.Year
            : null,
        },
        goalsOverview: {
          ...goalsOverviewSettings,
          mobileView: coerceGoalOverviewView(goalsOverviewSettings.mobileView),
        },
        ...(coercedFilters !== undefined ? { filters: coercedFilters } : {}),
        ...(coercedCustomTaskOrder !== undefined
          ? { customTaskOrder: coercedCustomTaskOrder }
          : {}),
      },
      limits: processLimits(
        data.limits as Record<string, RawLimitEntry> | null | undefined,
        isPremium,
      ),
      subscriptions: Array.isArray(data.subscriptions)
        ? (data.subscriptions as SavedUserSubscription[]).map(
            processSubscription,
          )
        : null,
    } as unknown as User;
  },
  toFirestore: (user: User) => user,
};
